import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  config as configApi,
  categories as catApi,
  products as prodApi,
  promotions as promoApi,
  type Category,
  type Product,
} from '../lib/api.js'
import { Button, Empty, Spinner } from '../components/ui/index.js'
import { cn } from '../lib/utils.js'
import { intlLocaleFor } from '@self-restaurant/shared'
import { useI18n } from '../lib/i18n.js'
import { currencyFor, displayTranslation } from './menu/shared.js'
import { pendingAttributesReview } from '../lib/attributes.js'
import { ProductCard } from './menu/ProductCard.js'
import { CategoryModal } from './menu/CategoryModal.js'
import { ProductModal } from './menu/ProductModal.js'
import { Link } from 'react-router-dom'
export function Menu() {
  const qc = useQueryClient()
  const { t, lang } = useI18n()
  const [selectedCat, setSelectedCat] = useState<string | null>(null)
  const [catModal, setCatModal] = useState<{ open: boolean; cat?: Category }>({ open: false })
  const [prodModal, setProdModal] = useState<{
    open: boolean
    prod?: Product
    focusPromo?: boolean
    // #489 M1: «Corrigir preço» no aviso do «No ar» a 0 abre o editor focado
    focusPrice?: boolean
  }>({
    open: false,
  })

  const { data: cfg } = useQuery({
    queryKey: ['config'],
    queryFn: () => configApi.get(),
  })

  // #419/#413: moeda do PAÍS do tenant (cfg.currency); fallback pelo idioma base
  const primaryLocale = cfg?.primary_locale ?? 'pt'
  const currency = cfg?.currency ?? currencyFor(primaryLocale)
  // #428: locale SÓ para o Intl (formato de preços) — língua base + país
  const priceLocale = intlLocaleFor(primaryLocale, cfg?.country_code)
  const timezone = cfg?.timezone ?? null

  const { data: cats = [], isLoading: loadingCats } = useQuery({
    queryKey: ['categories'],
    queryFn: () => catApi.list(),
  })

  const { data: prods = [], isLoading: loadingProds } = useQuery({
    queryKey: ['products', selectedCat],
    queryFn: () => prodApi.list(selectedCat ?? undefined),
  })

  // promoções ativas do tenant (#205): badge de estado e atalho "%" da lista.
  // Mesmo prefixo ['promotions'] do formulário — invalidar uma invalida todas.
  const { data: activePromos = [] } = useQuery({
    queryKey: ['promotions', 'active'],
    queryFn: () => promoApi.list(undefined, true),
  })
  const promoByProduct = new Map(activePromos.map((p) => [p.product_id, p]))

  // #216: quantos pratos têm sugestões por confirmar (atributos OU alergénios)
  const pendingCount = pendingAttributesReview(prods.filter((p) => p.is_active)).length

  if (loadingCats)
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner className="w-8 h-8" />
      </div>
    )

  const activeCats = cats.filter((c) => c.is_active)
  const shownProds = prods.filter((p) => p.is_active || !selectedCat)

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('admin.menu.title')}</h1>
          <p className="text-sm text-gray-500 mt-1">{t('admin.menu.subtitle')}</p>
        </div>
        <div className="flex gap-2 items-center">
          {/* #216: atalho para a fila única de revisão (sugestões pendentes) */}
          {pendingCount > 0 && (
            <Link to="../review-attributes">
              <Button
                variant="secondary"
                size="sm"
                data-testid="catalog-review-attributes-link"
                className="text-amber-700 border-amber-300 hover:bg-amber-50"
              >
                {t('admin.menu.review_attributes_link', { n: String(pendingCount) })}
              </Button>
            </Link>
          )}
          <Button
            variant="secondary"
            size="sm"
            data-testid="catalog-add-category"
            onClick={() => setCatModal({ open: true })}
          >
            {t('admin.menu.add_category')}
          </Button>
          <Button
            size="sm"
            data-testid="catalog-add-product"
            onClick={() => setProdModal({ open: true })}
            disabled={activeCats.length === 0}
          >
            {t('admin.menu.add_product')}
          </Button>
        </div>
      </div>

      {/* Categorias */}
      <div className="flex gap-2 flex-wrap">
        <button
          data-testid="catalog-category-all"
          onClick={() => setSelectedCat(null)}
          className={cn(
            'px-3 py-1.5 rounded-full text-sm font-medium transition-colors',
            selectedCat === null
              ? 'bg-indigo-600 text-white'
              : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50',
          )}
        >
          {t('admin.menu.all')}
        </button>
        {activeCats.map((cat) => {
          const name = displayTranslation(cat.translations, lang, primaryLocale)?.name ?? '—'
          return (
            <button
              key={cat.id}
              data-testid={`catalog-category-${cat.id}`}
              onClick={() => setSelectedCat(cat.id)}
              className={cn(
                'px-3 py-1.5 rounded-full text-sm font-medium transition-colors flex items-center gap-2',
                selectedCat === cat.id
                  ? 'bg-indigo-600 text-white'
                  : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50',
              )}
            >
              {name}
              <button
                data-testid={`catalog-category-edit-${cat.id}`}
                onClick={(e) => {
                  e.stopPropagation()
                  setCatModal({ open: true, cat })
                }}
                className="opacity-60 hover:opacity-100"
              >
                ✎
              </button>
            </button>
          )
        })}
      </div>

      {/* Produtos */}
      {loadingProds ? (
        <div className="flex items-center justify-center h-32">
          <Spinner />
        </div>
      ) : shownProds.length === 0 ? (
        <Empty
          title={t('admin.menu.empty_title')}
          description={t('admin.menu.empty_desc')}
          action={
            activeCats.length > 0 ? (
              <Button
                size="sm"
                data-testid="catalog-empty-add-product"
                onClick={() => setProdModal({ open: true })}
              >
                {t('admin.menu.add_product')}
              </Button>
            ) : (
              <Button
                size="sm"
                data-testid="catalog-empty-add-category"
                onClick={() => setCatModal({ open: true })}
              >
                {t('admin.menu.create_category_first')}
              </Button>
            )
          }
        />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {shownProds.map((p) => (
            <ProductCard
              key={p.id}
              product={p}
              promo={promoByProduct.get(p.id) ?? null}
              categories={cats}
              lang={lang}
              primaryLocale={primaryLocale}
              currency={currency}
              priceLocale={priceLocale}
              timezone={timezone}
              onEdit={(opts) =>
                setProdModal({
                  open: true,
                  prod: p,
                  ...(opts?.focusPrice ? { focusPrice: true } : {}),
                })
              }
              onEditPromo={() => setProdModal({ open: true, prod: p, focusPromo: true })}
              qc={qc}
            />
          ))}
        </div>
      )}

      {/* Modais */}
      <CategoryModal
        {...catModal}
        locale={primaryLocale}
        onClose={() => setCatModal({ open: false })}
        qc={qc}
      />
      <ProductModal
        {...prodModal}
        locale={primaryLocale}
        currency={currency}
        priceLocale={priceLocale}
        countryCode={cfg?.country_code ?? null}
        timezone={timezone}
        categories={cats.filter((c) => c.is_active)}
        {...(selectedCat ? { defaultCategoryId: selectedCat } : {})}
        onClose={() => setProdModal({ open: false })}
        qc={qc}
      />
    </div>
  )
}
